import axios from 'axios'
import type { RecruiterState, CandidateState } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export interface AuthUser {
  uuid: string
  username: string
  email: string
  role: 'superuser' | 'recruiter'
  must_change_password: boolean
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Session management helpers
export const sessionStorage = {
  // getRecruiterSessionId: (): string | null => {
  //   return localStorage.getItem('recruiter_session_id')
  // },
  // setRecruiterSessionId: (sessionId: string): void => {
  //   localStorage.setItem('recruiter_session_id', sessionId)
  // },
  getRecruiterUuid: (): string | null => {
    return localStorage.getItem('recruiter_uuid')
  },
  setRecruiterUuid: (uuid: string): void => {
    localStorage.setItem('recruiter_uuid', uuid)
  },
  getRecruiter: (): AuthUser | null => {
    const recruiterStr = localStorage.getItem('recruiter')
    return recruiterStr ? JSON.parse(recruiterStr) : null
  },
  setRecruiter: (recruiter: AuthUser): void => {
    localStorage.setItem('recruiter', JSON.stringify(recruiter))
  },
  getToken: (): string | null => {
    return localStorage.getItem('jwt_token')
  },
  setToken: (token: string): void => {
    localStorage.setItem('jwt_token', token)
  },
  clearFormDraft: (): void => {
    localStorage.removeItem('form_id')
  },
  clearAuth: (): void => {
    localStorage.removeItem('recruiter_uuid')
    localStorage.removeItem('recruiter')
    localStorage.removeItem('recruiter_session_id')
    localStorage.removeItem('jwt_token')
    localStorage.removeItem('form_id')
  },
}

// Add request interceptor to attach JWT token
api.interceptors.request.use(
  (config) => {
    try {
      const token = sessionStorage.getToken()
      
      // Add JWT token to Authorization header if token exists and is valid
      if (token && typeof token === 'string' && token.trim().length > 0) {
        // Ensure headers object exists (handles cases where headers might be undefined)
        if (!config.headers) {
          config.headers = {} as any
        }
        
        // Ensure headers is an object (not a string or other type)
        if (typeof config.headers !== 'object') {
          config.headers = {} as any
        }
        
        // Set Authorization header with proper typing
        // Using bracket notation to avoid TypeScript issues with dynamic header assignment
        // This ensures the header is set even when other headers (like Content-Type) are explicitly configured
        config.headers['Authorization'] = `Bearer ${token.trim()}`
      }
    } catch (error) {
      // If there's an error accessing token or setting headers, log it but don't break the request
      console.error('Error setting JWT token in request:', error)
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      sessionStorage.clearAuth()
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Recruiter API
export const recruiterApi = {
  // Get current state
  getState: async (formId?: string): Promise<{ form_id: string; state: RecruiterState }> => {
    const fid = formId // || sessionStorage.getRecruiterSessionId()
    const response = await api.post('/api/recruiter/', {
      form_id: fid || null,
    })
    return response.data
  },

  // Compose a batch of questions for the selected difficulty and types
  start: async (
    jd: string,
    difficulty: number,
    questionTypes: Array<'MCQ' | 'Open'>,
    formId?: string,
  ): Promise<{ status: string; form_id: string }> => {
    const fid = formId // || sessionStorage.getRecruiterSessionId()
    if (!fid) {
      throw new Error('form_id is required')
    }
    const response = await api.post('/api/recruiter/start', {
      jd,
      difficulty,
      question_types: questionTypes,
      form_id: fid,
    })
    // if (response.data.form_id) {
    //   // sessionStorage.setRecruiterSessionId(response.data.form_id)
    // }
    return response.data
  },

  // Keep or drop one proposed question
  decide: async (
    decision: 'approve' | 'reject',
    sessionId: string,
    questionIndex: number,
  ): Promise<{ status: string }> => {
    const response = await api.post('/api/recruiter/decide', {
      decision,
      form_id: sessionId,
      question_index: questionIndex,
    })
    return response.data
  },

  // Save approved questions to database
  save: async (sessionId: string): Promise<{ status: string; message: string; form_id: number }> => {
    const response = await api.post('/api/recruiter/save', {
      form_id: sessionId,
    })
    return response.data
  },

  // Remove an approved question by index
  removeQuestion: async (sessionId: string, questionIndex: number): Promise<{ status: string; message: string }> => {
    const response = await api.post('/api/recruiter/remove-question', {
      form_id: sessionId,
      question_index: questionIndex,
    })
    return response.data
  },

  // Get list of forms for a recruiter
  getForms: async (): Promise<{
    status: string
    forms: Array<{
      id: number
      uuid: string
      job_description: string | null
      questions: Array<any>
      created_at: string
      updated_at: string
    }>
  }> => {
    const response = await api.post('/api/recruiter/forms', {})
    return response.data
  },

  parseReadyTestExcel: async (file: File): Promise<{
    status: string
    emails: string[]
  }> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/recruiter/ready-test/parse-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  sendReadyTest: async (payload: {
    formUuid: string
    emails: string[]
    subject: string
    body: string
    frontendBaseUrl: string
  }): Promise<{
    status: string
    sent_count: number
    failed_count: number
    skipped_count: number
    failed_emails: string[]
    skipped_emails: string[]
    message: string
  }> => {
    const response = await api.post('/api/recruiter/ready-test/send', {
      form_uuid: payload.formUuid,
      emails: payload.emails,
      subject: payload.subject,
      body: payload.body,
      frontend_base_url: payload.frontendBaseUrl,
    })
    return response.data
  },

  getReadyTestInvite: async (inviteUuid: string): Promise<{
    status: string
    invite: {
      uuid: string
      job_description: string | null
      started: boolean
      sent_form_uuid: string | null
    }
  }> => {
    const response = await api.get(`/api/recruiter/ready-test/${inviteUuid}`)
    return response.data
  },

  startReadyTest: async (
    inviteUuid: string,
    details: { name: string; phone: string; email: string; cv: File }
  ): Promise<{
    status: string
    already_started: boolean
    sent_form_uuid: string
  }> => {
    const formData = new FormData()
    formData.append('name', details.name)
    formData.append('phone', details.phone)
    formData.append('email', details.email)
    formData.append('cv', details.cv)
    const response = await api.post(`/api/recruiter/ready-test/${inviteUuid}/start`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete a form
  deleteForm: async (formId: number): Promise<{
    status: string
    message: string
  }> => {
    const response = await api.delete('/api/recruiter/form', {
      data: {
        form_id: formId,
      },
    })
    return response.data
  },

  // Get list of sent forms for a recruiter
  getSentForms: async (): Promise<{
    status: string
    sentforms: Array<{
      id: number
      uuid: string
      recruiter_uuid: string
      candidate_uuid: string
      candidate_name: string | null
      candidate_email: string | null
      job_description: string | null
      questions: Array<any>
      review: string | null
      score: number | null
      complete: number
      created_at: string
      updated_at: string
    }>
  }> => {
    const response = await api.post('/api/recruiter/sentforms', {})
    return response.data
  },

  // Delete one or more sent forms by UUID
  deleteSentForms: async (uuids: string[]): Promise<{
    status: string
    message: string
    deleted_count: number
  }> => {
    const response = await api.post('/api/recruiter/sentforms/delete', { uuids })
    return response.data
  },

  // Professional managers
  getProfessionalManagers: async (): Promise<{
    status: string
    professional_managers: Array<{
      id: number
      uuid: string
      recruiter_id: number
      name: string
      email: string
      created_at: string
    }>
  }> => {
    const response = await api.post('/api/recruiter/professional-managers', {})
    return response.data
  },

  createProfessionalManager: async (name: string, email: string): Promise<{
    status: string
    message: string
    id: number
  }> => {
    const response = await api.post('/api/recruiter/professional-managers/create', { name, email })
    return response.data
  },

  deleteProfessionalManager: async (managerId: number): Promise<{
    status: string
    message: string
  }> => {
    const response = await api.post('/api/recruiter/professional-managers/delete', { manager_id: managerId })
    return response.data
  },

  sendFormsToManager: async (
    managerId: number,
    sentFormUuids: string[],
    subject?: string,
    body?: string,
    frontendBaseUrl?: string
  ): Promise<{ status: string; message: string }> => {
    const response = await api.post('/api/recruiter/professional-managers/send-forms', {
      manager_id: managerId,
      sent_form_uuids: sentFormUuids,
      subject,
      body,
      frontend_base_url: frontendBaseUrl,
    })
    return response.data
  },

  // Get candidate details for a sent form
  getSentFormCandidateDetails: async (sentFormUuid: string): Promise<{
    status: string
    candidate: {
      id: number
      uuid: string
      years_of_experience: number | null
      properties: any | null
    }
    sent_form: {
      uuid: string
      candidate_name: string
      job_description: string | null
      review: string | null
      score: number | null
      complete: number
      questions: Array<any>
      transcript: string | null
      created_at: string
      updated_at: string
    }
  }> => {
    const response = await api.get(`/api/recruiter/sentform/${sentFormUuid}/candidate-details`)
    return response.data
  },
}

// Authentication API
export const authApi = {
  // Register a new recruiter
  register: async (username: string, email: string, password: string): Promise<{ status: string; message: string; uuid: string }> => {
    const response = await api.post('/api/auth/register', {
      username,
      email,
      password,
    })
    return response.data
  },

  // Login
  login: async (username: string, password: string): Promise<{ status: string; message: string; token: string; recruiter: AuthUser }> => {
    const response = await api.post('/api/auth/login', {
      username,
      password,
    })
    return response.data
  },

  // Get current user info
  getCurrentUser: async (): Promise<{ status: string; recruiter: AuthUser }> => {
    const response = await api.get('/api/auth/me')
    return response.data
  },

  verifyPassword: async (currentPassword: string): Promise<{ status: string }> => {
    const response = await api.post('/api/auth/verify-password', {
      current_password: currentPassword,
    })
    return response.data
  },

  changePassword: async (newPassword: string, currentPassword?: string): Promise<{ status: string; message: string; recruiter: AuthUser }> => {
    const response = await api.post('/api/auth/change-password', {
      new_password: newPassword,
      current_password: currentPassword,
    })
    return response.data
  },

  createUser: async (username: string, email: string, password: string): Promise<{ status: string; message: string; uuid: string }> => {
    const response = await api.post('/api/auth/users', {
      username,
      email,
      password,
    })
    return response.data
  },

  listUsers: async (): Promise<{
    status: string
    users: Array<{
      uuid: string
      username: string
      email: string
      created_at: string
      must_change_password: boolean
    }>
  }> => {
    const response = await api.get('/api/auth/users')
    return response.data
  },

  updateUser: async (
    uuid: string,
    username: string,
    email: string,
    password?: string,
  ): Promise<{ status: string; message: string }> => {
    const response = await api.put(`/api/auth/users/${uuid}`, {
      username,
      email,
      password: password || undefined,
    })
    return response.data
  },

  deleteUser: async (uuid: string): Promise<{ status: string; message: string }> => {
    const response = await api.delete(`/api/auth/users/${uuid}`)
    return response.data
  },
}

// Candidate API
export const candidateApi = {
  // Get current interview state
  getState: async (formUuid: string): Promise<{ status: string; state: CandidateState }> => {
    const response = await api.get(`/api/candidate/${formUuid}`)
    return response.data
  },

  // Submit answer to current question. Duration is measured on the server.
  submitAnswer: async (formUuid: string, answer: string): Promise<{ status: string; state: CandidateState }> => {
    const response = await api.post(`/api/candidate/${formUuid}/answer`, {
      answer,
    })
    return response.data
  },

  // Hidden tab-leave signal. Failures are ignored by the caller.
  recordActivity: async (formUuid: string, event: 'hidden' | 'visible'): Promise<void> => {
    const url = `${API_BASE_URL}/api/candidate/${formUuid}/activity`
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true,
    })
  },

  // Export interview results
  export: async (formUuid: string): Promise<void> => {
    const response = await api.get(`/api/candidate/${formUuid}/export`, {
      responseType: 'blob',
    })
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'result.json'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    a.remove()
  },

  // Get list of candidates for a recruiter
  getCandidates: async (): Promise<{
    status: string
    candidates: Array<{
      id: number
      uuid: string
      recruiter_id: number
      name: string
      email: string | null
      phone: string | null
      years_of_experience: number | null
      cv_file_path: string
      extraction_status: string
      extraction_errors: string | null
      created_at: string
    }>
  }> => {
    const response = await api.post('/api/candidate/list', {})
    return response.data
  },

  // Delete a candidate
  deleteCandidate: async (candidateId: number): Promise<{
    status: string
    message: string
  }> => {
    const response = await api.delete('/api/candidate/', {
      data: {
        candidate_id: candidateId,
      },
    })
    return response.data
  },

  // Bulk delete candidates
  bulkDeleteCandidates: async (candidateIds: number[]): Promise<{
    status: string
    deleted_count: number
    message: string
  }> => {
    const response = await api.post('/api/candidate/bulk-delete', {
      candidate_ids: candidateIds,
    })
    return response.data
  },

  // Bulk email candidates
  bulkEmailCandidates: async (
    candidateIds: number[],
    subject: string,
    body: string,
    templateFormUuid?: string,
    frontendBaseUrl?: string
  ): Promise<{
    status: string
    sent_count: number
    failed_count: number
    failed_candidates: string[]
    generated_links?: Array<{
      candidate_name?: string | null
      candidate_email?: string | null
      sent_form_uuid?: string
      url?: string | null
    }>
    message: string
  }> => {
    const response = await api.post('/api/candidate/bulk-email', {
      candidate_ids: candidateIds,
      subject,
      body,
      template_form_uuid: templateFormUuid,
      form_uuid: templateFormUuid,
      frontend_base_url: frontendBaseUrl,
    })
    return response.data
  },

  // Update candidate contact details
  updateCandidate: async (
    candidateId: number,
    name: string,
    email?: string,
    phone?: string
  ): Promise<{
    status: string
    message: string
  }> => {
    const response = await api.patch('/api/candidate/update', {
      candidate_id: candidateId,
      name,
      email: email?.trim() || null,
      phone: phone?.trim() || null,
    })
    return response.data
  },

  // Extract CV to JSON
  extractCv: async (candidateId: number, prompt?: string): Promise<{
    status: string
    message: string
    properties?: any
  }> => {
    const response = await api.post('/api/recruiter/extract-cv', {
      candidate_id: candidateId,
      prompt,
    })
    return response.data
  },

  // Fetch candidate CV file
  getCandidateCv: async (candidateId: number): Promise<Blob> => {
    const response = await api.get(`/api/candidate/${candidateId}/cv`, {
      responseType: 'blob',
    })
    return response.data
  },
}

// Upload API
export const uploadApi = {
  // Upload candidate CV files
  uploadCandidates: async (
    files: File[]
  ): Promise<{
    status: string
    message: string
    summary: { total: number; success: number; failed: number }
    results: Array<{
      filename: string
      status: string
      candidate?: {
        name: string
        email?: string
        phone?: string
        years_of_experience?: number
      }
      errors?: string[]
      error?: string
    }>
  }> => {
    const formData = new FormData()
    
    files.forEach((file) => {
      formData.append('files', file)
    })

    const response = await api.post('/api/uploads/candidates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data
  },
}

export default api
